import Papa from 'papaparse';
import { hasOriginalText } from '../eventGroupTypes.js';
import {
    formatTimecode,
    formatSpeakers,
    formatTags,
    formatPlainText,
    formatYesNo,
    formatApproved,
    wrapValue,
    normalizeLineBreaks,
} from './formatters.js';

/**
 * @param {object} eventGroup
 * @param {import('./options.js').TranscriptExportOptions} options
 * @param {{ frameRate: number|string, dropFrame: boolean }} project
 * @param {import('./formatters.js').TranscriptLabelSet} labels
 * @returns {string}
 */
export function buildCsv(eventGroup, options, project, labels) {
    const enabledFields = (options.fieldLayout || []).filter((f) => f.enabled);
    const events = eventGroup.events || [];
    const preserve = Boolean(options.preserveLineBreaks);
    const lineBreak = preserve ? '\n' : ' ';

    const rows = events.map((event, count) => {
        /** @type {Record<string, string | number>} */
        const row = {};

        for (const field of enabledFields) {
            /** @type {string | number | undefined} */
            let value;
            switch (field.id) {
                case 'index':
                    value = count + 1;
                    break;
                case 'start':
                    value = formatTimecode(event.start, options.tcFormat, project);
                    break;
                case 'end':
                    value = formatTimecode(event.end, options.tcFormat, project);
                    break;
                case 'speakers':
                    value = formatSpeakers(event.speakers || []);
                    break;
                case 'text':
                    value = formatPlainText(event.text, lineBreak);
                    break;
                case 'original':
                    if (hasOriginalText(eventGroup.type)) {
                        value = formatPlainText(event.original, lineBreak);
                    }
                    break;
                case 'tags':
                    value = formatTags(event.tags || [], ' | ');
                    break;
                case 'notes':
                    value = normalizeLineBreaks(event.notes, preserve);
                    break;
                case 'notesCheck':
                    value = formatYesNo(Boolean(event.notesCheck), labels);
                    break;
                case 'reply':
                    value = normalizeLineBreaks(event.reply, preserve);
                    break;
                case 'replyCheck':
                    value = formatYesNo(Boolean(event.replyCheck), labels);
                    break;
                case 'approved':
                    value = formatApproved(event.approved, labels);
                    break;
                case 'forced':
                    value = formatYesNo(Boolean(event.forced), labels);
                    break;
                default:
                    break;
            }

            if (value === undefined) continue;
            row[field.id] = value === '' ? '' : wrapValue(String(value), field.wrap);
        }

        return row;
    });

    // Preserve column order from fieldLayout even when rows are empty
    const columns = enabledFields
        .map((f) => f.id)
        .filter((id) => id !== 'original' || hasOriginalText(eventGroup.type));

    if (rows.length === 0) {
        return Papa.unparse({ fields: columns, data: [] });
    }

    return Papa.unparse(rows, { columns });
}
